import { useAccount } from "@/hooks/useAccount";
import { useAuth } from "@/hooks/useAuth";
import { updateAppUser } from "@/services/appuser.queries";
import { useToastNotify } from "@/stores/toastStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { router } from "expo-router";

type DataFormType = { plate: string; nickname: string };

export const useVehicle = () => {
  const { user } = useAuth();
  const { notify } = useToastNotify();
  const queryClient = useQueryClient();

  const { account: accountData, isFetching: accountIsFetching, isSuccess: accountIsSuccess } = useAccount();

  const { mutate, isPending } = useMutation({
    mutationFn: updateAppUser,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["account"] });
      router.push("/my-vehicles");
    },
  });

  const addPlate = (data: DataFormType) => {
    if (accountIsSuccess && accountData) {
      const newPlate = {
        plate: data.plate,
        state: "XX",
        lastUsed: new Date().toISOString(),
        nickname: data.nickname,
      };

      const updatedPlates = [...(accountData.savedPlates || []), newPlate];

      const plateData = {
        ...accountData,
        savedPlates: updatedPlates,
        cognitoIDToken: user?.idToken ?? "",
      };

      mutate(plateData, {
        onError: (error) => {
          notify({
            severity: "error",
            message: t("An error occurred when creating new vehicle"),
          });
          console.error(`Error creating new vehicle: ${error}`);
        },
      });
    }
  };

  const getPlateToUpdate = (plate: string, state: string) => {
    if (accountIsSuccess && accountData) {
      return accountData.savedPlates.find((p) => p.plate === plate && p.state === state);
    }
  };

  const editPlate = (data: DataFormType, plate: string, state: string) => {
    if (accountIsSuccess && accountData) {
      const plateToUpdate = getPlateToUpdate(plate, state);

      if (!plateToUpdate) {
        notify({
          severity: "error",
          message: t("Plate not found"),
        });
        return;
      }

      const updatedPlate = { ...plateToUpdate, plate: data.plate, nickname: data.nickname };

      const updatedPlates = accountData.savedPlates.map((p) => (p.plate === plate && p.state === state ? updatedPlate : p));

      const plateData = {
        ...accountData,
        savedPlates: updatedPlates,
        cognitoIDToken: user?.idToken ?? "",
      };

      mutate(plateData, {
        onError: (error) => {
          notify({
            severity: "error",
            message: t("An error occurred when updating the vehicle"),
          });
          console.error(`An error occurred when updating the vehicle: ${error}`);
        },
      });

      return { plateToUpdate };
    }
  };

  const removePlate = (plate: string, state: string) => {
    if (accountIsSuccess && accountData) {
      const updatedPlates = accountData.savedPlates.filter((p) => p.plate !== plate || p.state !== state);

      const plateData = {
        ...accountData,
        savedPlates: updatedPlates,
        cognitoIDToken: user?.idToken ?? "",
      };

      mutate(plateData, {
        onError: (error) => {
          notify({
            severity: "error",
            message: t("An error occurred when removing the vehicle"),
          });
          console.error(`An error occurred when removing the vehicle: ${error}`);
        },
      });
    }
  };

  return {
    addPlate,
    editPlate,
    removePlate,
    getPlateToUpdate,
    isPending,
    accountIsFetching,
  };
};
